import { type CSSProperties, type TextareaHTMLAttributes, useState } from "react";
import { tokens } from "../../design-system/tokens";
import styles from "./Textarea.module.css";

type TextareaSize = "small" | "medium" | "large";
type TextareaColor = "secondary" | "outline" | "outlineLight" | "outlineSecondary";

interface TextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "color"> {
  width?: string | number;
  size?: TextareaSize;
  color?: TextareaColor;
  rtl?: boolean;
}

export default function Textarea({
  className,
  disabled = false,
  width,
  size = "large",
  color = "secondary",
  rtl = false,
  rows = 4,
  ...rest
}: TextareaProps) {
  const [isFocused, setIsFocused] = useState(false);

  const sizeConfig = {
    small: {
      minHeight: "60px",
      padding: "6px 10px",
      borderRadius: tokens.borderRadius.md,
      typography: tokens.typography.inputSmall,
    },
    medium: {
      minHeight: "72px",
      padding: "7px 11px",
      borderRadius: tokens.borderRadius.lg,
      typography: tokens.typography.inputMedium,
    },
    large: {
      minHeight: "90px",
      padding: "8px 12px",
      borderRadius: tokens.borderRadius.xl,
      typography: tokens.typography.inputLarge,
    },
  } as const;
  const currentSize = sizeConfig[size];

  const isOutline = color === "outline";
  const isOutlineLight = color === "outlineLight";
  const isOutlineSecondary = color === "outlineSecondary";
  const baseBackgroundColor = isOutline || isOutlineSecondary
    ? isOutlineSecondary && isFocused
      ? tokens.buttonColors.outlineSecondaryHoverBackground
      : "transparent"
    : isOutlineLight
      ? "transparent"
      : tokens.buttonColors.secondaryLight;
  const borderColor = isOutline
    ? tokens.colors.inputBorder
    : isOutlineSecondary
      ? isFocused
        ? tokens.buttonColors.secondary
        : tokens.buttonColors.outlineSecondary
      : isFocused
        ? tokens.colors.text.inverse
        : isOutlineLight
          ? tokens.buttonColors.secondaryLight
          : tokens.buttonColors.secondaryLight;
  const textColor = isOutlineLight
    ? tokens.colors.text.inverse
    : isOutline || isOutlineSecondary
      ? tokens.colors.text.primary
      : tokens.colors.primary;
  const placeholderColor = isOutlineLight
    ? tokens.colors.text.inverse
    : isOutline || isOutlineSecondary
      ? tokens.colors.text.primaryPlaceholder
      : tokens.colors.text.tertiary;
  const mergedClassName = className
    ? `${styles.textarea} ${className}`
    : styles.textarea;

  return (
    <textarea
      {...rest}
      rows={rows}
      disabled={disabled}
      className={mergedClassName}
      onChange={(event) => {
        rest.onChange?.(event);
      }}
      onFocus={(event) => {
        setIsFocused(true);
        rest.onFocus?.(event);
      }}
      onBlur={(event) => {
        setIsFocused(false);
        rest.onBlur?.(event);
      }}
      onKeyDown={(event) => {
        rest.onKeyDown?.(event);
      }}
      style={{
        "--input-placeholder-color": placeholderColor,
        width,
        minHeight: currentSize.minHeight,
        padding: currentSize.padding,
        borderRadius: currentSize.borderRadius,
        border: `1px solid ${borderColor}`,
        backgroundColor: baseBackgroundColor,
        color: textColor,
        fontFamily: currentSize.typography.fontFamily,
        fontWeight: currentSize.typography.fontWeight,
        fontSize: currentSize.typography.fontSize,
        lineHeight: String(currentSize.typography.lineHeight),
        textAlign: rtl ? "right" : "center",
        direction: rtl ? "rtl" : "ltr",
        transition: `background-color ${tokens.transitions.normal}, border-color ${tokens.transitions.normal}`,
        outline: "none",
        boxSizing: "border-box",
        resize: "vertical",
      } as CSSProperties}
    />
  );
}
